import { useState, useEffect, useRef, useCallback, useMemo, memo} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPhone, faPhoneSlash } from "@fortawesome/free-solid-svg-icons"
import * as Style from "./IncomingCallModalStyle"
import { useCallContext } from "./CallContext"
import CallDuration from "./CallDuration"
import { BetaBadge } from "./CallingThreadStyle";

export function IncomingCallModal({ onClose }) {
    const [position, setPosition] = useState({ x: window.innerWidth - 350, y: 20 })
    const [isDragging, setIsDragging] = useState(false)
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
    const modalRef = useRef(null)

    const { activeCall, setActiveCall, acceptIncomingCall, rejectIncomingCall, terminateActiveCall, acceptingCall, rejectingCall, isTerminating } = useCallContext()

    const handleMouseDown = useCallback((e) => {
        // start dragging if clicking on the draggable area (not buttons)
        if (e.target.closest('button')) return
        
        setIsDragging(true)
        const rect = modalRef.current.getBoundingClientRect()
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        })
    }, [])

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return

        const newX = e.clientX - dragOffset.x
        const newY = e.clientY - dragOffset.y

        // viewport
        const maxX = window.innerWidth - (modalRef.current?.offsetWidth || 320)
        const maxY = window.innerHeight - (modalRef.current?.offsetHeight || 150)

        setPosition({
            x: Math.max(0, Math.min(newX, maxX)),
            y: Math.max(0, Math.min(newY, maxY))
        })
    }, [isDragging, dragOffset.x, dragOffset.y])

    const handleMouseUp = useCallback(() => {
        setIsDragging(false)
    }, [])

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            return () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
            }
        }
    }, [isDragging, dragOffset])

    const displayName = useMemo(() => 
        activeCall?.member?.externalProfile?.name || activeCall?.member?.externalId
    , [activeCall?.member?.externalProfile?.name, activeCall?.member?.externalId]);

    const displayNumber = useMemo(() => {
        let tempNumber = activeCall?.member?.externalId;

        if (!tempNumber) {
            tempNumber = activeCall?.callType === "OUTGOING" ? activeCall?.callInfo?.to: activeCall?.callInfo?.from;
        } 

        if (!tempNumber) {
            tempNumber = activeCall?.callType === "OUTGOING" ? activeCall?.to : activeCall?.from
        }

        return tempNumber;
    }, [
        activeCall?.member?.externalId,
        activeCall?.callType,
        activeCall?.to,
        activeCall?.from,
        activeCall?.callInfo?.to,
        activeCall?.callInfo?.from,
    ]);

    const handleCallReject = useCallback(async () => {
        await rejectIncomingCall(activeCall)
        setActiveCall(null)
        onClose()
    }, [activeCall, rejectIncomingCall, setActiveCall, onClose])

    const handleCallAccept = useCallback(async () => {
        await acceptIncomingCall(activeCall)
    }, [activeCall, acceptIncomingCall])

    const handleCallTerminate = useCallback(async () => {
        await terminateActiveCall()
    }, [terminateActiveCall])

    const isRejecting = useMemo(() => {
        if (!rejectingCall) return false
        return rejectingCall?.callId === activeCall?.callId
    }, [rejectingCall?.callId, activeCall?.callId])

    const isAccepting = useMemo(() => {
        if (!acceptingCall) return false
        return acceptingCall?.callId === activeCall?.callId
    }, [acceptingCall?.callId, activeCall?.callId])

    // no active call
    if (!activeCall) return null

    console.log("activeCall: ", activeCall)

    return (
        <Style.ModalOverlay>
            <Style.ModalContainer
                ref={modalRef}
                style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onMouseDown={handleMouseDown}
                isDragging={isDragging}
            >
                <Style.Header>
                    <Style.HeaderContent>
                        WhatsApp Call 
                        <BetaBadge>BETA</BetaBadge>
                    </Style.HeaderContent>

                    <GripDotVerticalIcon />
                </Style.Header>
                
                <Style.ContentWrapper>
                    <Style.IconWrapper>
                        <Style.WhatsAppIcon>
                            <FontAwesomeIcon icon={faPhone} />
                        </Style.WhatsAppIcon>
                    </Style.IconWrapper>
                    
                    <Style.CallInfo>
                        <Style.CallerName>{displayName || displayNumber}</Style.CallerName>
                        <Style.PhoneNumber>{displayNumber}</Style.PhoneNumber>
                    </Style.CallInfo>

                    <CallingStatus activeCall={activeCall} />
                </Style.ContentWrapper>

                <Style.ActionButtons>
                    {activeCall.callType === "INCOMING" && activeCall.callStatus ==="RINGING" && <IncomingCallActions isAccepting={isAccepting} isRejecting={isRejecting} handleAcceptCall={handleCallAccept} handleRejectCall={handleCallReject} />}
                    {(activeCall.callType === "OUTGOING" || activeCall.callStatus ==="ACCEPTED") && <OngoingAndOutgoingCallAction handleCallTerminate={handleCallTerminate} isTerminating={isTerminating} />}
                </Style.ActionButtons>
            </Style.ModalContainer>
        </Style.ModalOverlay>
    )
}

const IncomingCallActions = memo(({handleAcceptCall, handleRejectCall, isDisabled, isRejecting, isAccepting}) => {
    return (
        <>
            {!isRejecting && <Style.AcceptButton onClick={handleAcceptCall} disabled={isDisabled || isRejecting || isAccepting}>
                <FontAwesomeIcon icon={faPhone} />
                {isAccepting ? "Accepting..": "Accept Call"}
            </Style.AcceptButton>}
            {!isAccepting && <Style.DeclineButton onClick={handleRejectCall} disabled={isDisabled || isRejecting || isAccepting}>
                <FontAwesomeIcon icon={faPhoneSlash} />
                {isRejecting ? "Terminating..": "Decline Call"}
            </Style.DeclineButton>}
        </>
    )
})
IncomingCallActions.displayName = 'IncomingCallActions'

const OngoingAndOutgoingCallAction = memo(({handleCallTerminate, isTerminating, isDisabled}) => {
    return (
        <Style.DeclineButton onClick={handleCallTerminate} disabled={isDisabled || isTerminating}>
            <FontAwesomeIcon icon={faPhoneSlash} />
            {isTerminating ? "Ending Call.." : "End Call"}
        </Style.DeclineButton>
    )
})
OngoingAndOutgoingCallAction.displayName = 'OngoingAndOutgoingCallAction'


const CallingStatus = memo(({activeCall}) => {
    const {callType, callStatus} = activeCall

    if (callType === "INCOMING" && callStatus === "RINGING") {
        return <Style.CallStatus>Incoming calling...</Style.CallStatus>
    }
    
    if (callType === "OUTGOING" && callStatus === "INPROGRESS") {
        return <Style.CallStatus>Call in progress...</Style.CallStatus>
    }

    if (callType === "OUTGOING" && callStatus === "INITIATING") {
        return <Style.CallStatus>Outgoing calling...</Style.CallStatus>
    }

    if (callStatus === "ACCEPTED") {
        return (
            <CallDuration activeCall={activeCall}>
                {({ duration, formatDuration }) => (
                    <Style.CallStatus>
                        {formatDuration(duration)}
                    </Style.CallStatus>
                )}
            </CallDuration>
        )
    }

    return null
})
CallingStatus.displayName = 'CallingStatus'

const GripDotVerticalIcon = ({ size=18 }) => (
    <svg opacity={0.2} height={size} width={size} fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512">
        <path d="M48 144a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm0 160a48 48 0 1 0 0-96 48 48 0 1 0 0 96zM96 416A48 48 0 1 0 0 416a48 48 0 1 0 96 0zM208 144a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm48 112a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM208 464a48 48 0 1 0 0-96 48 48 0 1 0 0 96z"/>
    </svg>
)